import React, { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Box, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DialogActionButtons } from '../../components/dialogActionButtons/DialogActionButtons';
import { FormAddress } from '../../components/formAddress/FormAddress';
import { DescriptionForm } from '../../components/forms/decriptionForm/DescriptionForm';
import { PhotoForm } from '../../components/forms/photoForm/PhotoForm';
import { Header } from '../../components/header/Header';
import { useAlert } from '../../hooks/useAlert';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { createOpportunities, getProfile, getRequiredQualification } from '../../store/actions/actions';
import { routesNames } from '../../utils/constants';
import { convertWeekdays, weekdays } from '../../utils/data';
import { convertedArray, convertedArrayReverse } from '../../utils/methods';
import {
  mainHeader, mainWrapper, opportunityBlock,
  opportunityBlockPhoto, opportunityBtns, useStyles,
} from './createOpportunity.styles';
import { IFormValues } from './types';
import { SocketContext } from '../../contexts/SocketContext';
import { messengerEvents } from '../../websocket/events';
import { ModalSuccess } from '../../components/modals/modalSuccess/ModalSuccess';
import { organizerSlice } from '../../store/slice/slice';
import { Link } from 'react-router-dom';

dayjs.extend(utc);

export const CreateOpportunity = (): JSX.Element => {
  const { control, register, handleSubmit, formState: { errors }, reset, setError, setValue, clearErrors } = useForm<IFormValues>();
  const profile = useAppSelector((state) => state.organizerReducer.profile);
  const [selectedPhoto, setSelectedPhoto] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [categories, setCategories] = React.useState<string[]>([]);
  const [qualifications, setQualifications] = React.useState<string[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const [days, setDays] = React.useState<string[]>([weekdays[0]]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const classes = useStyles();
  const { sendMessage } = useContext(SocketContext);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);

  useEffect(() => {
    getProfile()(dispatch);
    getRequiredQualification()(dispatch);
  }, []);

  useEffect(() => {
    if (profile) {
      setCategories(convertedArrayReverse([profile.category]));
      setValue('categories', convertedArrayReverse([profile.category]));
    }
  }, [profile]);

  const onSubmitted = (): void => {
    setSubmitted(true);
  };

  const actionProps = {
    submitDisabled,
    cancel: 'CANCEL',
    submit: 'CREATE OPPORTUNITY',
    handleClose: (): void => navigate(routesNames.main),
    handleSubmit: onSubmitted,
  };

  const descriptionProps = {
    errors,
    register,
    setValue,
    submitted,
    categories,
    setCategories,
    required: true,
    qualifications,
    setQualifications,
  };

  const cleanStates = (): void => {
    setSubmitted(false);
    setSelectedPhoto('');
    setCategories([]);
    setQualifications([]);
  };

  const formProps = {
    days,
    errors,
    setDays,
    register,
    setValue,
    setError,
    clearErrors,
    submitted,
    required: true,
    control,
  };

  const onSubmit = (data: IFormValues): void => {
    const startTime = dayjs(data.startTime).utc();
    const startDate = dayjs(data.firstOccurrence)
      .utc()
      .hour(startTime.get('hour'))
      .minute(startTime.get('minute'))
      .format();

    const endTime = dayjs(data.endTime).utc();
    const endDate = dayjs(data.firstOccurrence)
      .utc()
      .hour(endTime.get('hour'))
      .minute(endTime.get('minute'))
      .format();

    const sendData: IFormValues = {
      frequency: convertWeekdays[days[0]],
      pictureUrl: selectedPhoto,
      categories: convertedArray(data.categories),
      maximumVolunteers: Number(data.maximumVolunteers),
      name: data.name,
      address: data.address,
      contactName: data.contactName,
      contactEmail: data.contactEmail,
      details: data.details,
      cancellationPolicy: data.cancellationPolicy,
      startDate,
      endDate,
    };

    if (data.contactPhone && data.contactPhone?.length > 4) {
      sendData.contactPhone = data.contactPhone;
    }
    if (data.qualifications) {
      sendData.qualifications = data.qualifications;
    }
    if (categories.length <= 3 && selectedPhoto) {
      setSubmitDisabled(true);
      createOpportunities(sendData)(dispatch).then(() => {
        dispatch(organizerSlice.actions.setSuccessVisible(true));
        navigate(routesNames.main);
        cleanStates();
        reset();
        sendMessage(JSON.stringify({ event: messengerEvents.getChats }));
        setSubmitDisabled(false);
      });
    }
  };

  return (
    <>
      <Header />
      <div className={classes.mainBlock}>
        <Box sx={mainWrapper} component="form" ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <Box sx={mainHeader}>
            <div className={classes.mainTypography}>
              <Typography
                variant="h6"
                marginBottom="8px"
              >Create new opportunity
              </Typography>
            </div>
            <div className={classes.childTypography}>
              <Typography
                variant="body1"
                component="a"
                onClick={() => navigate('/')}
                sx={{ cursor: 'pointer' }}
              >
                My opportunities /
              </Typography>
              <Typography
                variant="body2"
              >Create new opportunity
              </Typography>
            </div>
          </Box>
          <Box sx={opportunityBlock}>
            <Typography variant="h6">Information</Typography>
            <FormAddress {...formProps} />
          </Box>
          <Box sx={opportunityBlockPhoto}>
            <Box >
              <Typography variant="h6">Photo</Typography>
            </Box>
            <PhotoForm
              submitted={submitted}
              selectedPhoto={selectedPhoto}
              setSelectedPhoto={setSelectedPhoto}
            />
          </Box>
          <Box sx={opportunityBlock}>
            <Box>
              <Typography variant="h6">Description</Typography>
            </Box>
            <DescriptionForm {...descriptionProps} />
          </Box>
          <Box sx={opportunityBtns}>
            <DialogActionButtons
              {...actionProps} />
          </Box>
        </Box>
      </div>
    </>
  );
};
